import {
    Configuration,
    AuthApi,
    AccountsApi,
    ReportsApi,
    CampaignsApi,
    AccountsApiInterface,
    AuthApiInterface,
    ReportsApiInterface,
    CampaignsApiInterface,
    TemplatesApiInterface,
    TemplatesApi,
    EmailsApi,
    EmailsApiInterface,
    FilesApi,
    FilesApiInterface,
    DomainsApi,
    DomainsApiInterface,
    ValidEmailsApi,
    ValidEmailsApiInterface,
    SecurityApi,
    SecurityApiInterface,
    ContactsApi,
    ContactsApiInterface,
    PoolsApi,
    PoolsApiInterface,
    InboundRouteApi,
    InboundRouteApiInterface,
    WebhooksApi,
    WebhooksApiInterface,
    ChannelsApiInterface,
    ChannelsApi,
    TasksApi,
    TasksApiInterface,
    ProductApiInterface,
    ProductApi,
    ListsApiInterface,
    SegmentsApiInterface,
    ListsApi,
    SegmentsApi,
    NotificationApi,
    NotificationApiInterface,
    UsersApi,
    UsersApiInterface,
    PaymentsApi,
    PaymentsApiInterface,
} from '@dev/ee-api';

import { session, local } from 'store2';
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import axiosRetry from 'axios-retry';

import { store } from '@shared-store/shared-store';
import Authentication from '../Authentication';
import { ShowDialog } from '@api/store/dialog/actions';
import { get } from 'lodash-es';
import { StorageKeysEnum } from '../dictionaries/StorageKeys';
import { ShowDialogParams } from '@api/store/dialog/types';

interface ConfParams {
    basePath: string;
    apiKey?: string;
    baseOptions?: any;
    headers?: any;
}

interface RequestInterface {
    Accounts: AccountsApiInterface;
    Auth: AuthApiInterface;
    Statistics: ReportsApiInterface;
    Channels: ChannelsApiInterface;
    Campaigns: CampaignsApiInterface;
    Templates: TemplatesApiInterface;
    Emails: EmailsApiInterface;
    Files: FilesApiInterface;
    Domain: DomainsApiInterface;
    ValidEmails: ValidEmailsApiInterface;
    Security: SecurityApiInterface;
    Contacts: ContactsApiInterface;
    Pools: PoolsApiInterface;
    Inbound: InboundRouteApiInterface;
    Webhooks: WebhooksApiInterface;
    Tasks: TasksApiInterface;
    Products: ProductApiInterface;
    Reports: ReportsApiInterface;
    Lists: ListsApiInterface;
    Segments: SegmentsApiInterface;
    Notification: NotificationApiInterface;
    Users: UsersApiInterface;
    Payments: PaymentsApiInterface;
}

type AdditionalRequestOptions = {
    disableImpersonation?: boolean;
};

type RequestOptions = AxiosRequestConfig & AdditionalRequestOptions;

const API_CALL_COUNT = 2;
const RETRY_COUNT = API_CALL_COUNT - 1;
const DEFAULT_CALL_TIMEOUT = 60000;
const INCREASED_CALL_TIMOUT = 60000;
const RETRY_DELAY = 1000;

const showTimeoutModal = (err) => {
    if (err.code === 'ECONNABORTED') {
        const showDialogParams = {
            title: 'Connection timeout',
            body: 'Looks like the server is taking to long to respond, this can be caused by either poor connectivity or an error with our servers. Please try again in a while.',

            confirmLabel: 'Ok',
        };

        console.error(`connection timeout -> url: ${err?.config?.url}, method: ${err?.config?.method}, timeout: ${err?.config?.timeout}ms`);

        store.dispatch(ShowDialog(showDialogParams));
    }
};

const logoutFromApp = () => {
    Authentication.removeAccessToken();

    const path = `${location.origin}/login`;
    window.location.href = path;
};

const showSessionExpiredModal = () => {
    const showDialogParams: ShowDialogParams = {
        title: 'Your Session Has Expired',
        body: 'Your session has expired and you must log in again.',
        onClickConfirm: logoutFromApp,
        confirmLabel: 'Ok',
        onClose: logoutFromApp,
        onClickCancel: logoutFromApp,
    };

    store.dispatch(ShowDialog(showDialogParams));
};

/**
 * Method used for request call configuration, returns category api handlers
 */
export const request = (options: RequestOptions = {}): RequestInterface => {
    let headers = {};

    if (!options.disableImpersonation && session.has('subaccountemailtouse')) {
        headers = { ...headers, ...{ 'X-ElasticEmail-ImpersonateAs': session.get('subaccountemailtouse') } };
    }

    if (local.has('browserToken')) {
        headers = { ...headers, ...{ 'X-ElasticEmail-BrowserToken': local.get('browserToken') } };
    }

    if (!session.get(StorageKeysEnum.SubaccountEmailToUse)) {
        headers = { ...headers, ...{ 'X-ElasticEmail-Fingerprint': local.get(StorageKeysEnum.Fingerprint) } };
    }

    const getParams = (): ConfParams => {
        const baseOptions = {
            ...{
                timeout: DEFAULT_CALL_TIMEOUT,
            },
            ...options,
        };

        const params: ConfParams = {
            basePath: `${APIPATH}/v3`,
            baseOptions,
            headers,
        };

        if (Authentication.isAuthenticated()) {
            params.apiKey = Authentication.getAccessToken();
        }

        return params;
    };

    const conf = new Configuration(getParams());

    axios.defaults.headers.common = { ...axios.defaults.headers.common, ...headers };

    const client: AxiosInstance = axios.create();

    axiosRetry(client, {
        retries: API_CALL_COUNT,
        retryDelay: () => RETRY_DELAY,
        retryCondition: (err) => {
            const currentRetryCount = get(err, 'config.axios-retry.retryCount') || API_CALL_COUNT;

            if (err?.response?.status === 410) {
                showSessionExpiredModal();
                return false;
            }

            if (currentRetryCount >= RETRY_COUNT) {
                showTimeoutModal(err);
                return false;
            }

            if (err.code === 'ECONNABORTED') {
                client.interceptors.request.use((config) => ({
                    ...config,
                    timeout: INCREASED_CALL_TIMOUT,
                }));
                return true;
            }

            return false;
        },
        shouldResetTimeout: true,
    });

    const Accounts = new AccountsApi(conf, null, client);
    const Auth = new AuthApi(conf, null, client);
    const Statistics = new ReportsApi(conf, null, client);
    const Channels = new ChannelsApi(conf, null, client);
    const Campaigns = new CampaignsApi(conf, null, client);
    const Templates = new TemplatesApi(conf, null, client);
    const Emails = new EmailsApi(conf, null, client);
    const Files = new FilesApi(conf, null, client);
    const Domain = new DomainsApi(conf, null, client);
    const ValidEmails = new ValidEmailsApi(conf, null, client);
    const Security = new SecurityApi(conf, null, client);
    const Contacts = new ContactsApi(conf, null, client);
    const Pools = new PoolsApi(conf, null, client);
    const Inbound = new InboundRouteApi(conf, null, client);
    const Webhooks = new WebhooksApi(conf, null, client);
    const Tasks = new TasksApi(conf, null, client);
    const Products = new ProductApi(conf, null, client);
    const Reports = new ReportsApi(conf, null, client);
    const Lists = new ListsApi(conf, null, client);
    const Segments = new SegmentsApi(conf, null, client);
    const Notification = new NotificationApi(conf, null, client);
    const Users = new UsersApi(conf, null, client);
    const Payments = new PaymentsApi(conf, null, client);

    return {
        Accounts,
        Statistics,
        Channels,
        Campaigns,
        Auth,
        Templates,
        Emails,
        Files,
        Security,
        Domain,
        ValidEmails,
        Contacts,
        Pools,
        Inbound,
        Webhooks,
        Tasks,
        Products,
        Reports,
        Segments,
        Lists,
        Notification,
        Users,
        Payments,
    };
};
